import React, { useState, useRef, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { isMobileViewport } from "../utils/layoutBreakpoints";

const CustomDropdown = ({
  options = [],
  value,
  onChange,
  placeholder = "Select an option",
  disabled = false,
  className = "",
  name = "",
  id = "",
  required = false,
  renderOption = null,
  getDisplayValue = null,
  renderValue = null,
  searchable = false,
  ...props
}) => {
  const isControlled = value !== undefined;
  const [isOpen, setIsOpen] = useState(false);
  const [selectedValue, setSelectedValue] = useState(isControlled ? value : null);
  const [searchQuery, setSearchQuery] = useState("");
  const [isMobile, setIsMobile] = useState(() => isMobileViewport());
  const [menuStyle, setMenuStyle] = useState(null);
  const dropdownRef = useRef(null);
  const menuRef = useRef(null);
  const searchInputRef = useRef(null);

  useEffect(() => {
    if (isControlled) {
      setSelectedValue(value);
    }
  }, [value, isControlled]);

  useEffect(() => {
    const handleResize = () => {
      const mobile = isMobileViewport();
      setIsMobile(mobile);
      if (isOpen && mobile !== isMobile) {
        setIsOpen(false);
        setSearchQuery("");
      }
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [isOpen, isMobile]);

  const updateMobileMenuPosition = useCallback(() => {
    const trigger = dropdownRef.current?.querySelector(".custom-dropdown-trigger");
    if (!trigger) return;

    const rect = trigger.getBoundingClientRect();
    const padding = 12;
    const gap = 4;
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.visualViewport?.height || window.innerHeight;
    const viewportOffsetTop = window.visualViewport?.offsetTop || 0;

    const width = Math.min(Math.max(rect.width, 200), viewportWidth - padding * 2);
    let left = rect.left;
    if (left + width > viewportWidth - padding) {
      left = viewportWidth - width - padding;
    }
    if (left < padding) {
      left = padding;
    }

    const maxMenuHeight = Math.min(320, viewportHeight * 0.5);
    const spaceBelow = viewportOffsetTop + viewportHeight - rect.bottom - padding;
    const spaceAbove = rect.top - viewportOffsetTop - padding;
    const openAbove = spaceBelow < 140 && spaceAbove > spaceBelow;
    const available = openAbove ? spaceAbove - gap : spaceBelow - gap;
    const maxHeight = Math.max(120, Math.min(maxMenuHeight, available));
    const top = openAbove
      ? Math.max(padding, rect.top - maxHeight - gap)
      : rect.bottom + gap;

    setMenuStyle({
      position: "fixed",
      top: `${top}px`,
      left: `${left}px`,
      width: `${width}px`,
      maxHeight: `${maxHeight}px`,
      zIndex: 10000,
    });
  }, []);

  useEffect(() => {
    if (!isOpen || !isMobile) {
      setMenuStyle(null);
      return undefined;
    }

    updateMobileMenuPosition();
    const frame = requestAnimationFrame(updateMobileMenuPosition);

    window.addEventListener("scroll", updateMobileMenuPosition, true);
    window.addEventListener("resize", updateMobileMenuPosition);
    window.visualViewport?.addEventListener("resize", updateMobileMenuPosition);
    window.visualViewport?.addEventListener("scroll", updateMobileMenuPosition);

    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener("scroll", updateMobileMenuPosition, true);
      window.removeEventListener("resize", updateMobileMenuPosition);
      window.visualViewport?.removeEventListener("resize", updateMobileMenuPosition);
      window.visualViewport?.removeEventListener("scroll", updateMobileMenuPosition);
    };
  }, [isOpen, isMobile, searchQuery, options.length, updateMobileMenuPosition]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setIsOpen(false);
        setSearchQuery("");
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || isMobile) return undefined;

    const handleClickOutside = (event) => {
      const isClickOutsideDropdown =
        dropdownRef.current && !dropdownRef.current.contains(event.target);
      const isClickOutsideMenu = menuRef.current && !menuRef.current.contains(event.target);
      const isClickOnOption = event.target.closest(".custom-dropdown-option");

      if (isClickOutsideDropdown && isClickOutsideMenu && !isClickOnOption) {
        setIsOpen(false);
        setSearchQuery("");
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen, isMobile]);

  const closeMenu = () => {
    setIsOpen(false);
    setSearchQuery("");
  };

  const handleToggle = () => {
    if (disabled) return;
    if (isOpen) {
      closeMenu();
      return;
    }
    setIsOpen(true);
    if (searchable && !isMobileViewport()) {
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 50);
    }
  };

  const handleSelect = (option, event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    closeMenu();
    setSelectedValue(option);
    if (onChange) {
      onChange(option);
    }
  };

  const filteredOptions =
    searchable && searchQuery
      ? options.filter((option) => {
          const optionLabel = typeof option === "object" ? option.label : option;
          const optionValue = typeof option === "object" ? option.value : option;
          const searchLower = searchQuery.toLowerCase().trim();
          const searchDigits = searchQuery.replace(/\D/g, "");

          const matchesLabel =
            optionLabel && optionLabel.toString().toLowerCase().includes(searchLower);
          const matchesValue =
            optionValue && optionValue.toString().toLowerCase().includes(searchLower);

          let matchesData = false;
          if (typeof option === "object" && option.data) {
            const data = option.data;
            const info =
              data.user_information ||
              data.userInformation ||
              data.USER_INFORMATION ||
              {};
            const searchableFields = [
              data.TRADING_ID,
              data.account?.account_information?.TRADING_ID,
              data.ID,
              data.id,
              data.NAME,
              data.name,
              data.NICK_NAME,
              data.NICK,
              data.CURRENCY_CODE,
              data.currencies?.CURRENCY_CODE,
              data.CURRENCY_SYMBOL,
              data.currencies?.CURRENCY_SYMBOL,
              data.PHONE,
              data.PHONE_NUMBER,
              data.MOBILE,
              data.MOBILE_NUMBER,
              data.phone,
              data.phone_number,
              data.mobile,
              data.mobile_number,
              data.TEL,
              data.tel,
              info.PHONE,
              info.PHONE_NUMBER,
              info.MOBILE,
              info.MOBILE_NUMBER,
            ];

            matchesData = searchableFields.some((field) => {
              if (field === null || field === undefined) return false;
              const text = field.toString().toLowerCase();
              if (text.includes(searchLower)) return true;
              if (searchDigits.length >= 3) {
                const fieldDigits = text.replace(/\D/g, "");
                if (fieldDigits && fieldDigits.includes(searchDigits)) return true;
              }
              return false;
            });
          }

          return matchesLabel || matchesValue || matchesData;
        })
      : options;

  const getDisplayValueFunc = () => {
    const currentValue = isControlled ? value : selectedValue;

    if (getDisplayValue && typeof getDisplayValue === "function") {
      return getDisplayValue(currentValue);
    }

    if (currentValue === null || currentValue === undefined) return placeholder;

    if (typeof currentValue === "object" && currentValue.label) {
      return currentValue.label;
    }

    if (typeof currentValue === "object" && currentValue.value !== undefined) {
      return currentValue.value;
    }

    return currentValue;
  };

  const isOptionSelected = (option) => {
    const optionValue = typeof option === "object" ? option.value : option;
    const activeValue = isControlled ? value : selectedValue;
    return (
      (typeof activeValue === "object" && activeValue?.value === optionValue) ||
      activeValue === optionValue ||
      activeValue === option
    );
  };

  const renderSearch = () => {
    if (!searchable) return null;
    return (
      <div className="custom-dropdown-search">
        <i className="fa-solid fa-search custom-dropdown-search-icon"></i>
        <input
          ref={searchInputRef}
          type="text"
          className="custom-dropdown-search-input"
          placeholder="Search..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onClick={(e) => e.stopPropagation()}
        />
      </div>
    );
  };

  const renderOptionsList = () => {
    if (filteredOptions.length === 0) {
      return <div className="custom-dropdown-empty">No options available</div>;
    }

    return filteredOptions.map((option, index) => {
      const optionLabel = typeof option === "object" ? option.label : option;
      const isSelected = isOptionSelected(option);
      const optionClass = `custom-dropdown-option ${
        isSelected ? "custom-dropdown-option-selected" : ""
      }`;

      if (renderOption) {
        return (
          <div
            key={index}
            role="option"
            aria-selected={isSelected}
            className={optionClass}
            onClick={(e) => handleSelect(option, e)}
          >
            {renderOption(option, isSelected)}
          </div>
        );
      }

      return (
        <div
          key={index}
          role="option"
          aria-selected={isSelected}
          className={optionClass}
          onClick={(e) => handleSelect(option, e)}
        >
          <span className="custom-dropdown-option-text" dir="auto">
            {optionLabel}
          </span>
          {isSelected && <i className="fa-solid fa-check custom-dropdown-check"></i>}
        </div>
      );
    });
  };

  const renderMenu = () => {
    if (!isOpen) return null;

    const menu = (
      <div
        className={`custom-dropdown-menu${isMobile ? " custom-dropdown-menu--mobile" : ""}`}
        ref={menuRef}
        role="listbox"
        style={isMobile ? menuStyle : undefined}
      >
        {renderSearch()}
        {renderOptionsList()}
      </div>
    );

    if (!isMobile || typeof document === "undefined" || !document.body) {
      return menu;
    }

    return createPortal(
      <div className="custom-dropdown-mobile-root" role="presentation">
        <button
          type="button"
          className="custom-dropdown-mobile-backdrop"
          aria-label="Close"
          onClick={closeMenu}
        />
        {menuStyle ? menu : null}
      </div>,
      document.body
    );
  };

  const currentValue = isControlled ? value : selectedValue;

  return (
    <div
      className={`custom-dropdown ${className} ${isOpen ? "custom-dropdown-open" : ""}`}
      ref={dropdownRef}
      {...props}
    >
      <button
        type="button"
        className="custom-dropdown-trigger"
        onClick={handleToggle}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        <span className="custom-dropdown-value">
          {renderValue ? (
            currentValue ? (
              renderValue(currentValue, placeholder)
            ) : (
              <span className="custom-dropdown-placeholder">{placeholder}</span>
            )
          ) : (
            <span className="custom-dropdown-value-text" dir="auto">
              {getDisplayValueFunc()}
            </span>
          )}
        </span>
        <i className={`fa-solid fa-chevron-${isOpen ? "up" : "down"} custom-dropdown-arrow`}></i>
      </button>

      {renderMenu()}
    </div>
  );
};

export default CustomDropdown;
